// The branch dialogs (spec C8, S17; kit Dialog): switch to the Non-renewal or Policy inactive steps
// with the reason and the effective date, and Back to renewal steps with its reason. Opened from the
// Record's "..." menu; on confirm the dialog closes and the toast offers Undo (spec C7 dialogs).
import { useState } from 'react';
import type { BranchReason, RenewalBranch, RenewalDetail } from '../auth/api';
import { Dialog } from '../shell/Dialog';
import { branchNames, branchReasonHelp, branchReasonLabels, typedDate } from './branch-lines';
import { parseDate } from './fix-lines';

/** The longest reason the server takes. */
const reasonMax = 300;

const sublineOf = (r: RenewalDetail) =>
  [r.insured, r.carrier ?? 'Carrier not in AMS360', r.line, r.policyNumber].join(' | ');

/** Switch to a branch: the reason (Non-renewal) and the effective date, typed. */
export function BranchDialog({
  r,
  branch,
  onClose,
  onSave,
}: {
  r: RenewalDetail;
  branch: RenewalBranch;
  onClose: () => void;
  /** True once saved; the reason it was not, otherwise. */
  onSave: (b: { reason?: BranchReason; effectiveDate: string }) => Promise<true | string>;
}) {
  const nonRenewal = branch === 'non_renewal';
  const [reason, setReason] = useState<BranchReason | null>(null);
  // A non-renewal takes effect when the term ends; a cancellation date is typed in.
  const [date, setDate] = useState(nonRenewal ? typedDate(r.expirationDate) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const day = date.trim() ? parseDate(date) : null;
  const ready = !!day && (!nonRenewal || !!reason);
  const save = async () => {
    if (!day) return;
    setBusy(true);
    const result = await onSave({ ...(reason ? { reason } : {}), effectiveDate: day });
    setBusy(false);
    if (result !== true) setError(result);
  };
  return (
    <Dialog
      id="br-t"
      title={branchNames[branch]}
      subline={sublineOf(r)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary ml-auto"
            disabled={!ready || busy}
            onClick={() => void save()}
          >
            {nonRenewal ? 'Switch to non-renewal steps' : 'Switch to policy inactive steps'}
          </button>
        </>
      }
    >
      <p className="m0 t13 text2 lh">
        {nonRenewal
          ? 'The carrier is not renewing. The steps become: upload the notice, email the insured, log the activity, hand to the AE. Status stays Pending until the AE accepts.'
          : 'The policy is inactive on the carrier site but active in AMS360. The steps become: upload the notice, email the AE, log the activity, close as Cancelled.'}
      </p>
      {nonRenewal && (
        <div role="radiogroup" aria-labelledby="br-r">
          <div id="br-r" className="flab">
            Reason <span className="req">*</span>
          </div>
          <div className="fopts">
            {(Object.keys(branchReasonLabels) as BranchReason[]).map((k) => (
              <button
                key={k}
                type="button"
                className={reason === k ? 'chip on' : 'chip'}
                aria-pressed={reason === k}
                onClick={() => setReason(k)}
              >
                {branchReasonLabels[k]}
              </button>
            ))}
          </div>
          {reason && <p className="fnote">{branchReasonHelp[reason]}</p>}
        </div>
      )}
      <div>
        <label className="flab" htmlFor="br-date">
          {nonRenewal ? 'Non-renewal effective' : 'Cancelled effective'}{' '}
          <span className="req">*</span>
        </label>
        <input
          id="br-date"
          className="input"
          type="text"
          placeholder="Dec 20, 2026"
          autoComplete="off"
          aria-invalid={date.trim() && !day ? 'true' : 'false'}
          value={date}
          onChange={(e) => {
            setDate(e.target.value);
            setError(null);
          }}
        />
        {date.trim() && !day && (
          <p className="ferr" role="alert">
            Enter a date like Dec 20, 2026.
          </p>
        )}
      </div>
      {error && (
        <p className="ferr" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/** Back to renewal steps: why, required (spec C8). The renewal steps come back as they were. */
export function BackDialog({
  r,
  branch,
  onClose,
  onSave,
}: {
  r: RenewalDetail;
  branch: RenewalBranch;
  onClose: () => void;
  onSave: (reason: string) => Promise<true | string>;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const text = reason.trim();
  const save = async () => {
    setBusy(true);
    const result = await onSave(text);
    setBusy(false);
    if (result !== true) setError(result);
  };
  return (
    <Dialog
      id="bk-t"
      title="Back to renewal steps"
      subline={sublineOf(r)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary ml-auto"
            disabled={!text || busy}
            onClick={() => void save()}
          >
            Back to renewal steps
          </button>
        </>
      }
    >
      <div>
        <label className="flab" htmlFor="bk-reason">
          Why does it leave the {branchNames[branch].toLowerCase()} steps?{' '}
          <span className="req">*</span>
        </label>
        <textarea
          id="bk-reason"
          className="input"
          placeholder="For example: the carrier withdrew the non-renewal"
          maxLength={reasonMax}
          value={reason}
          onChange={(e) => {
            setReason(e.target.value);
            setError(null);
          }}
        />
        <p className="fnote">
          The renewal steps come back where they were. The reason goes in the Portal log.
        </p>
      </div>
      {error && (
        <p className="ferr" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}
